/**
 * "Your personal data" (S-32, LFPDPPP arts. 21-34): a client asks to see, correct, delete or object to
 * the use of their personal data, and reads here what became of each request. The answer is delivered
 * on this page; the e-mail only says there is an update.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import { calendarDate } from '../../components/client/enteringFund.helpers';
import dataRequestsService, {
  type DataRequest,
  type DataRight,
} from '../../services/dataRequests.service';
import {
  RIGHT_HELP,
  RIGHT_LABEL,
  dateLine,
  rightsToAsk,
  shownRight,
  standing,
} from '../../lib/dataRequests';
import { getApiErrorMessage } from '../../lib/apiError';
import { isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import StepUpCodeField from '../../components/security/StepUpCodeField';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';

const MIN_TEXT = 10;

/** A gold button that looks unavailable while it is. */
const PRIMARY_BUTTON = 'text-sm px-3 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

function Answer({ request }: Readonly<{ request: DataRequest }>): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const download = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await dataRequestsService.accessCopyUrl(request.id, code.trim()));
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, 'We could not prepare the download. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (!request.response_text) return null;
  return (
    <div className="mt-3 rounded-lg p-3" style={{ background: colors.surfaceMuted }}>
      <p className="text-xs font-semibold mb-1" style={{ color: colors.muted }}>Our answer</p>
      <p className="text-sm whitespace-pre-wrap" style={{ color: colors.ink }}>{request.response_text}</p>
      {request.has_access_copy && (
        <div className="mt-3 max-w-sm">
          <StepUpCodeField
            id={`dl-${request.id}`}
            value={code}
            onChange={setCode}
            hint="It confirms that it is you who downloads your data."
          />
          <button
            type="button"
            disabled={busy || !isMfaCode(code.trim())}
            onClick={() => { void download(); }}
            className={`mt-3 ${PRIMARY_BUTTON}`}
            style={{ background: colors.gold, color: colors.navy }}
          >
            Download my data
          </button>
        </div>
      )}
      {error && (
        <p className="text-sm mt-2" role="alert" style={{ color: colors.danger }}>{error}</p>
      )}
    </div>
  );
}

function RequestCard({ request }: Readonly<{ request: DataRequest }>): JSX.Element {
  const line = dateLine(request, calendarDate);
  return (
    <li
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>{RIGHT_LABEL[request.right]}</p>
          <p className="text-xs" style={{ color: colors.muted }}>
            Asked {formatDateTime(request.received_at)}
          </p>
        </div>
        <StatusBadge tone={request.status === 'received' ? 'neutral' : 'success'}>
          {standing(request)}
        </StatusBadge>
      </div>
      <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>{request.description}</p>
      {line && <p className="text-xs mt-2" style={{ color: colors.muted }}>{line}</p>}
      <Answer request={request} />
      {request.blocked_until && (
        <p className="text-xs mt-3" style={{ color: colors.muted }}>
          What the law requires us to keep is set aside and used only for legal purposes until{' '}
          {calendarDate(request.blocked_until)}.
        </p>
      )}
    </li>
  );
}

function AskForm({
  rights,
  onAsked,
}: Readonly<{ rights: DataRight[]; onAsked: () => void }>): JSX.Element {
  const [picked, setPicked] = useState<DataRight>(rights[0]);
  const right = shownRight(picked, rights);
  const [description, setDescription] = useState('');
  const [change, setChange] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsChange = right === 'rectification';
  const ready =
    description.trim().length >= MIN_TEXT &&
    (!needsChange || change.trim().length >= 3) &&
    isMfaCode(code.trim());

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await dataRequestsService.ask({
        right,
        description: description.trim(),
        mfa_code: code.trim(),
        ...(needsChange ? { requested_change: change.trim() } : {}),
      });
      setDescription('');
      setChange('');
      setCode('');
      onAsked();
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, 'We could not send your request. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="rounded-2xl p-5 mb-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      onSubmit={(e) => { e.preventDefault(); void submit(); }}
    >
      <label className="block text-xs mb-1" htmlFor="data-right" style={{ color: colors.muted }}>
        What would you like to do?
      </label>
      <select
        id="data-right"
        value={right}
        onChange={(e) => setPicked(e.target.value as DataRight)}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ border: `1px solid ${colors.border}` }}
      >
        {rights.map((r) => <option key={r} value={r}>{RIGHT_LABEL[r]}</option>)}
      </select>
      <p className="text-xs mt-2" style={{ color: colors.muted }}>{RIGHT_HELP[right]}</p>

      <label className="block text-xs mt-4 mb-1" htmlFor="data-description" style={{ color: colors.muted }}>
        Tell us which data and what you ask
      </label>
      <textarea
        id="data-description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        maxLength={2000}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ border: `1px solid ${colors.border}` }}
      />
      {needsChange && (
        <>
          <label className="block text-xs mt-4 mb-1" htmlFor="data-change" style={{ color: colors.muted }}>
            What should it say?
          </label>
          <textarea
            id="data-change"
            value={change}
            onChange={(e) => setChange(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}` }}
          />
        </>
      )}
      <div className="mt-4 max-w-sm">
        <StepUpCodeField
          id="data-code"
          value={code}
          onChange={setCode}
          hint="It confirms that it is you who makes this request."
        />
      </div>
      <button
        type="submit"
        disabled={busy || !ready}
        className={`mt-4 ${PRIMARY_BUTTON}`}
        style={{ background: colors.gold, color: colors.navy }}
      >
        Send my request
      </button>
      {error && (
        <p className="text-sm mt-3" role="alert" style={{ color: colors.danger }}>{error}</p>
      )}
    </form>
  );
}

export default function DataRequests(): JSX.Element {
  const [requests, setRequests] = useState<DataRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRequests(await dataRequestsService.mine());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'We could not load your requests. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const available = rightsToAsk(requests);
  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Your personal data"
        subtitle="Ask to see, correct, delete or object to the use of the personal data we hold about you. We answer within 20 business days."
      />
      {error && <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>}
      {available.length > 0 ? (
        <AskForm rights={available} onAsked={() => { void reload(); }} />
      ) : (
        <p className="mb-6 text-sm" style={{ color: colors.muted }}>
          You have an open request for each right. You can ask again once one is done.
        </p>
      )}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && requests.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>You have not made any request.</p>
      )}
      <ul className="space-y-3">
        {requests.map((r) => <RequestCard key={r.id} request={r} />)}
      </ul>
    </div>
  );
}
